<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import { useNavigate } from 'svelte-navigator';

  import { AppState, SetIconsBackground, ToastQueue } from '../etc/stores';
  import { Container } from '../services/container';
  import { Auth } from '../services/auth';
  import { Backend } from '../services/backend';

  import Page from '../components/misc/Page.svelte';
  import Button from '../components/form/Button.svelte';
  import BackArrow from '../components/misc/BackArrow.svelte';
  import TextInput from '../components/form/TextInput.svelte';
  import LockIndicator from '../components/icons/LockIndicator.svelte';
  import { Logger } from '../services/logger';
  import { _ } from 'svelte-i18n';
  import { extractMessageFromError } from '../services/api';
  import Analytics from '../services/analytics';
  import { SessionManager } from '../services/session-manager';
  import { isMobile } from '../etc/utils';
  import { Wix } from '../services/wix';
  import Heading from '../components/typography/Heading.svelte';
  import Label from '../components/typography/Label.svelte';
  import sessionKeys from '../etc/sessionKeys';

  const t = (text: string) => {
    return $_(`connect_email.${text}`);
  };

  const navigate = useNavigate();
  const backend = Container.get(Backend);
  const auth = Container.get(Auth);
  const logger = Container.get(Logger).context(
    'ConnectAccount.svelte'
  ) as Logger;
  const session = Container.get(SessionManager) as SessionManager;

  let loading = false;
  let input;

  const { providerData = {} } = $AppState.auth;

  const { firstName, lastName } = providerData;

  let emailInput = '';
  const { category, prefixEvent } = Analytics.getConnectAccountData();

  const signIn = async () => {
    try {
      loading = true;
      Analytics.logEvent(`${prefixEvent}-signin`, category);
      const emailExists = await backend.verifyIfEmailExists(emailInput);

      if (!emailExists.found) {
        ToastQueue.add({
          title: $_('error.title'),
          message: t('email_not_exists_error'),
          type: 'error',
          duration: 5000
        });

        return;
      }

      const id = await auth.requestCodeTarget(emailInput, 'email');
      session.remove(sessionKeys.COLLECT_COINS);
      session.remove(sessionKeys.GUEST);
      navigate('/code', {
        state: {
          method: {
            customerId: null,
            type: 'email',
            value: emailInput,
            name: firstName || '',
            lastname: lastName || '',
            firstCoins: null,
            validationCode: { id }
          },
          onResend: () => {
            return auth.requestCodeTarget(emailInput, 'email');
          }
        }
      });
    } catch (e) {
      logger.error(e);

      ToastQueue.add({
        title: $_('error.title'),
        message: extractMessageFromError(e),
        type: 'error',
        duration: 5000
      });
    } finally {
      loading = false;
    }
  };

  const focusInput = () => {
    setTimeout(() => {
      if (input) {
        input.focus();
      }
    }, 50);
  };

  onMount(() => {
    if (isMobile()) {
      if (auth.provider === 'wix') {
        Wix.togglePageMenu();
      }
    }

    Analytics.logPageView(
      `Connect Email${Analytics.getFreeCoinsTitleFlow()}`,
      '/connect-email'
    );
    focusInput();
    SetIconsBackground('light');
  });
</script>

<Page padding="80px 24px 16px">
  <BackArrow />
  <LockIndicator />
  <div class="connectAccount__title">
    <Heading type="h2" textAlign="center">{t('title')}</Heading>
  </div>
  <div class="connectAccount__description">
    <Label type="default-regular" textAlign="center" color="#7A89B0">
      {t('description')}
    </Label>
  </div>
  <div class="connectAccount__input">
    <TextInput
      bind:value={emailInput}
      type="email"
      label={t('email')}
      name="email"
      placeholder="marilyn@mail.com"
      showCheckOnBlur
    />
  </div>
  <div class="connectAccount__button">
    <Button
      type="button"
      on:click={signIn}
      disabled={!emailInput}
      label={t('btn')}
      {loading}
    />
  </div>
</Page>

<style lang="scss">
  div {
    width: 100%;
  }
  .connectAccount {
    &__title {
      margin-top: 40px;
    }

    &__description {
      margin-top: 16px;
    }

    &__input {
      margin-top: 60px;
    }

    &__button {
      margin-top: 24px;
    }
  }
</style>
